/*global chrome*/

import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import jwtDecode from 'jwt-decode'
import axios from 'axios'

import { register } from '../../services/authService'

import { TextField, PasswordField } from './Fields'
import { Logo } from '../LandingPage/Logo'
import { SlimLayout } from './SlimLayout'
import TwoFactorVerification from './TwoFactorVerification'
import ReactivateModal from './ReactivateModal'
import Turnstile, { TURNSTILE_TOKEN_FIELD } from './Turnstile'
import { baseURL } from '../../config'
import { getSharedLoadReturnPath } from '../../utils/sharedLoadReturn'
import { trackEvent } from '../../utils/analytics'

import './authPages.css'
import googleIcon from '../../media/icons/google.svg'
import { startSession } from '../../services/sessionCookie'

const sendTokenToChromeExtension = (extensionId, token) => {
	if (chrome && chrome.runtime && chrome.runtime.sendMessage) {
		chrome.runtime.sendMessage(extensionId, token)
	} else {
		console.log('Chrome extension messaging API not available.')
	}
}

export default function SignUp({
	sourceExtension,
	setSourceExtension,
	windowOpender,
	userToken,
}) {
	let location = useLocation()
	const navigate = useNavigate()

	const queryParams = new URLSearchParams(location.search)
	const returnTo = sourceExtension ? null : getSharedLoadReturnPath(location.search)
	const redirectUri = returnTo || queryParams.get('redirect_uri')

	const devURI = 'http://localhost:5001'
	const productionURI = ''
	const authUrl = !baseURL ? '' : baseURL
	// Check if we're on localhost and adjust the URL accordingly
	let googleAuthUrl = `${
		window.location.hostname === 'localhost' ? 'http://localhost:5001' : authUrl
	}/auth/google`

	const googleAuthParams = new URLSearchParams()
	if (redirectUri) googleAuthParams.set('redirect_uri', redirectUri)
	if (sourceExtension && userToken) {
		googleAuthParams.set('source', 'extension')
		googleAuthParams.set('token', userToken)
	}
	if (googleAuthParams.toString()) googleAuthUrl += `?${googleAuthParams}`

	const [loading, setLoading] = useState(false)
	// const [sourceExtension, setSourceExtension] = useState(false)

	const [state, setState] = useState({
		email: '',
		password: '',
		password2: '',
		errorMsg: null,
		loading: false,
		typeChanged: false,
		displayGoogleBtn: true,
		errorText: '',
		agreedToTerms: false,
	})
	const [showPassword, setShowPassword] = useState(false)
	const [twoFactorData, setTwoFactorData] = useState(null)
	const [reactivationData, setReactivationData] = useState(null)
	const [turnstileToken, setTurnstileToken] = useState('')
	const [turnstileResetSignal, setTurnstileResetSignal] = useState(0)
	const [turnstileStatus, setTurnstileStatus] = useState('loading')
	const [turnstileAttempt, setTurnstileAttempt] = useState(0)

	const onChange = e => {
		e.preventDefault()

		const { name, value } = e.target
		setState({ ...state, [name]: value })
	}

	// Stable identity: Turnstile re-renders its widget when this callback changes
	const handleTurnstileStatus = useCallback(status => setTurnstileStatus(status), [])

	const showError = error => {
		setLoading(false)
		setState(prevState => ({
			...prevState,
			errorText: error,
		}))

		return setTimeout(() => {
			setState(prevState => ({
				...prevState,
				errorText: '',
			}))
		}, 8000)
	}

	const handleTwoFactorSuccess = data => {
		if (data.token) startSession(data.token)

		if (sourceExtension) {
			const extensionWindow = windowOpender || window.opener

			if (!data.extensionToken) {
				setTwoFactorData(null)
				showError('Extension authentication failed: No extension token received')
				return
			}

			if (!extensionWindow) {
				setTwoFactorData(null)
				showError('Extension authentication failed: extension window unavailable')
				return
			}

			extensionWindow.postMessage(
				{ source: 'loadfetcherLogin', token: data.extensionToken },
				'*'
			)
			window.close()
			return
		}

		if (data.token) {
			if (redirectUri) {
				window.location.href = decodeURIComponent(redirectUri)
			} else {
				window.location = '/payment'
			}
		}
	}

	const onSubmit = async e => {
		showError('')
		e.preventDefault()

		// Check if user agreed to terms
		if (!state.agreedToTerms) {
			return showError('You must agree to the Terms & Conditions and Privacy Policy to sign up.')
		}

		// Check if passwords match
		if (state.password !== state.password2) {
			return showError('Passwords do not match.')
		}

		// Block only once the widget is rendered and solvable. If it never
		// loaded, let the request through and let the server decide.
		if (turnstileStatus === 'ready' && !turnstileToken) {
			return showError('Please complete the verification check below.')
		}

		try {
			setLoading(true)
			const extensionContext = sourceExtension
				? { source: 'extension', token: userToken }
				: {}
			const result = await register({
				...state,
				agreedToTerms: true,
				[TURNSTILE_TOKEN_FIELD]: turnstileToken,
				...extensionContext,
			})
			setLoading(false)

			if (result?.requiresTwoFactor) {
				setTwoFactorData({
					pendingLoginToken: result.pendingLoginToken,
					maskedEmail: result.maskedEmail,
				})
			}
		} catch (err) {
			setLoading(false)
			// The token is spent whether or not the request succeeded
			setTurnstileResetSignal(signal => signal + 1)

			if (err.response?.status === 400 && err.response?.data) {
				const errors = err.response.data
				const firstError = errors.error || Object.values(errors)[0]
				if (firstError) return showError(firstError)
			}

			showError('An unexpected error occurred. Please try again.')
		}
	}

	useEffect(() => {
		const allowedOrigins = [
			'https://loadfetcher.com',
			window.location.origin,
			window.location.hostname === 'localhost' ? 'http://localhost:5001' : '',
		].filter(Boolean)

		const handlePostMessage = event => {
			if (!allowedOrigins.includes(event.origin)) return


			if (event.data?.requiresReactivation) {
				setReactivationData({
					reactivateToken: event.data.reactivateToken,
					purgeAt: event.data.purgeAt,
				})
				return
			}

			const token = event.data?.token
			const extensionToken = event.data?.extensionToken

			const params = new URLSearchParams(location.search)

			if (!token) return

			startSession(token)
			if (event.data.email) localStorage.setItem('email', event.data.email)

			if (sourceExtension && windowOpender) {
				if (!extensionToken) {
					showError('Extension authentication failed: No extension token received')
					return
				}

				windowOpender?.postMessage(
					{
						source: 'loadfetcherLogin',
						token: extensionToken,
					},
					'*'
				)

				setTimeout(() => {
					window.close()
				}, 1000)
			} else if (params.get('source') === 'extension' && window.opener) {
				if (!extensionToken) {
					showError('Extension authentication failed: No extension token received')
					return
				}

				setSourceExtension(true)

				window.opener.postMessage(
					{
						source: 'loadfetcherLogin',
						token: extensionToken,
					},
					'*'
				)

				setTimeout(() => {
					window.close()
				}, 1000)
			} else {
				const user = token && jwtDecode(token)
				const trial_end = new Date(user?.trial_end)
				const period_end = new Date(user?.period_end)

				const now = new Date()
				const isExpired = period_end < now || trial_end < now
				const productionExtensionId = 'ihcgicdogclbieclnldfjmlglkolankb'
				const developmentExtensionId = 'omgobnjbempcolkjnfphklponcmlooko'
				const extensionId = productionExtensionId

				if (token && event.data.email && !isExpired) {
					trackEvent('sign_up', { method: 'google' })
					if (extensionToken)
						sendTokenToChromeExtension(extensionId, {
							extensionToken,
						})

					if (redirectUri) {
						window.location = redirectUri
					} else {
						window.location = '/loadboard'
					}
				} else if (isExpired) {
					trackEvent('sign_up', { method: 'google' })
					if (extensionToken)
						sendTokenToChromeExtension(extensionId, {
							extensionToken,
						})

					if (redirectUri) {
						window.location = redirectUri
					} else {
						window.location = '/plans'
					}
				}
			}
		}

		window.addEventListener('message', handlePostMessage)

		return () => {
			window.removeEventListener('message', handlePostMessage)
		}
	}, [location])

	useEffect(() => {
		setState(prevState => ({
			...prevState,
			password2: prevState.password,
		}))
	}, [state.password])

	if (reactivationData) {
		return (
			<ReactivateModal
				purgeAt={reactivationData.purgeAt}
				// Only Google reaches this screen, and its handshake is already
				// spent — the token the callback handed over is what stands in.
				onConfirm={async () => {
					const res = await axios.post(`${baseURL}/api/auth/reactivate`, {
						reactivateToken: reactivationData.reactivateToken,
					})
					return res.data
				}}
				onCancel={() => setReactivationData(null)}
				onRestored={handleTwoFactorSuccess}
			/>
		)
	}

	if (twoFactorData) {
		return (
			<TwoFactorVerification
				pendingLoginToken={twoFactorData.pendingLoginToken}
				maskedEmail={twoFactorData.maskedEmail}
				onCancel={() => setTwoFactorData(null)}
				onSuccess={data => {
					trackEvent('sign_up', { method: 'email' })
					handleTwoFactorSuccess(data)
				}}
			/>
		)
	}

	return (
		<SlimLayout>
			{!sourceExtension && (
				<div className="flex">
					<>
						<Link to="/" aria-label="Home">
							<Logo
								color={'text-gray-600 text-lg'}
								height={'45px'}
								width={'45px'}
							/>
						</Link>
					</>
				</div>
			)}

			<h2 className="mt-6 mb-4 text-lg font-semibold text-gray-900">
				Get started today
			</h2>

			<button
				className="google-button w-full inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-indigo-500"
				type="button"
				onClick={() => {
					if (!state.agreedToTerms) {
						showError('You must agree to the Terms & Conditions and Privacy Policy to sign up.')
						return;
					}

					const w = 500
					const h = 600
					const y = window.top.outerHeight / 2 + window.top.screenY - h / 2
					const x = window.top.outerWidth / 2 + window.top.screenX - w / 2

					window.open(
						googleAuthUrl,
						'Google Sign-In',
						`toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
					)
				}}
			>
				<img
					src={googleIcon}
					alt="Google"
					style={{ width: '20px', height: '20px' }}
				/>

				<p className="btn-text">Continue with Google</p>
			</button>

			<div className="divider text-gray-800">OR</div>

			<form
				onSubmit={onSubmit}
				className="mt-1 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2"
			>
				{state.errorText && (
					<div className="col-span-full text-red-500 text-sm -mb-4">
						{state.errorText}
					</div>
				)}

				<TextField
					className="col-span-full -my-2"
					label="Full name"
					id="name"
					name="name"
					type="text"
					autoComplete="given-name"
					required
					onChange={onChange}
				/>

				<TextField
					className="col-span-full -my-2"
					label="Email address"
					id="email"
					name="email"
					type="email"
					autoComplete="email"
					required
					onChange={onChange}
				/>
				<PasswordField
					className="col-span-full -my-2"
					label="Password"
					id="password"
					name="password"
					autoComplete="new-password"
					required
					onChange={onChange}
					showPassword={showPassword}
					onTogglePassword={() => setShowPassword(!showPassword)}
				/>

				<div className="col-span-full flex items-start">
					<input
						type="checkbox"
						id="agreedToTerms"
						name="agreedToTerms"
						checked={state.agreedToTerms}
						onChange={(e) => setState({ ...state, agreedToTerms: e.target.checked })}
						className="mt-1 mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
						required
					/>
					<label htmlFor="agreedToTerms" className="text-sm text-gray-700">
						I agree to LoadFetcher's{' '}
						<Link to="/terms" target="_blank" className="font-medium text-blue-600 hover:underline">
							Terms & Conditions
						</Link>{' '}
						and{' '}
						<Link to="/policy" target="_blank" className="font-medium text-blue-600 hover:underline">
							Privacy Policy
						</Link>
					</label>
				</div>

				<div className="col-span-full">
					<Turnstile
						key={turnstileAttempt}
						onVerify={setTurnstileToken}
						onStatus={handleTurnstileStatus}
						resetSignal={turnstileResetSignal}
					/>

					{turnstileStatus === 'unavailable' && (
						<div className="mt-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
							We couldn't load our security check. It's usually an ad blocker or a
							corporate network blocking{' '}
							<code className="text-xs">challenges.cloudflare.com</code>.
							<div className="mt-2">
								Use <b>Continue with Google</b> above, allow that domain and{' '}
								<button
									type="button"
									className="font-medium text-blue-600 hover:underline"
									onClick={() => {
										setTurnstileStatus('loading')
										setTurnstileAttempt(n => n + 1)
									}}
								>
									try again
								</button>
								.
							</div>
						</div>
					)}
				</div>

				<div className="col-span-full">
					<button
						type="submit"
						disabled={loading}
						className="submit-button group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus:outline-none focus-visible:outline-blue-600 w-full disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed"
					>
						{loading && (
							<span className="loading loading-spinner loading-md mr-4"></span>
						)}

						<span>
							Sign Up <span aria-hidden="true">&rarr;</span>
						</span>
					</button>

					<p className="mt-6 text-sm text-gray-700">
						Already registered?{' '}
						<Link
							to={`/account/login${
								sourceExtension ? '?source=extension&token=' + userToken : returnTo ? '?next=' + encodeURIComponent(returnTo) : ''
							}`}
							className="font-medium text-blue-600 hover:underline"
						>
							Sign in
						</Link>{' '}
						to your account.
					</p>
				</div>
			</form>
		</SlimLayout>
	)
}
